import { 
  Brain, Volume2, Activity, Eye, Compass, GraduationCap, 
  Briefcase, Heart, Cpu, Sparkles, Waves, Stethoscope, 
  Users, Layers, Baby, Sun, Gamepad2, FileText, Globe
} from 'lucide-react';

export interface DisabilityCategory {
  id: string;
  name: string;
  shortName: string;
  icon: any;
  color: string;
  badgeColor: string;
  rpwdAlignment: string;
  dsmCode: string;
  icfCode: string;
  departmentName: string;
  targetAssessments: string[];
  description: string;
  clinicalGoals: string[];
  primaryModuleIds: string[];
  secondaryModuleIds: string[];
  accommodations: string[];
  sensoryPreset: {
    dwellMs?: number;
    fontMode?: 'standard' | 'opendyslexic';
    contrastMode?: 'standard' | 'high-contrast-yellow';
    audioVoiceLang?: string;
    ambientSoundscape?: string;
  };
}

export const DISABILITY_CLUSTERS: DisabilityCategory[] = [
  {
    id: 'autism-neuro',
    name: 'Autism Spectrum & Neurodevelopmental Disorders',
    shortName: 'Autism & Sensory',
    icon: Brain,
    color: 'from-amber-500 to-orange-600',
    badgeColor: 'bg-amber-100 text-amber-900 border-amber-300',
    rpwdAlignment: 'Autism Spectrum Disorder, Intellectual Disability (RPwD 2016)',
    dsmCode: '299.00 (F84.0)',
    icfCode: 'b122 (Global psychosocial functions), d710 (Basic interpersonal interactions)',
    departmentName: 'Department of Autism & Behavioral Sciences',
    targetAssessments: ['VB-MAPP Milestone Radar', 'CARS-2 Childhood Autism Rating', 'Sensory Profile 2'],
    description: 'Specialized interventions for autistic learners, sensory processing needs, and behavioral development.',
    clinicalGoals: [
      'Expressive functional communication through symbol-based AAC',
      'Positive behavior reinforcement via structured token economies',
      'Sensory regulation and meltdown prevention in Snoezelen environments',
      'Milestone evaluation using the standardized 170-item VB-MAPP battery'
    ],
    primaryModuleIds: ['autism', 'vbmapp-assessment', 'sensory-room', 'animal-therapy', 'early-intervention'],
    secondaryModuleIds: ['aac', 'core-vocab-ai', 'music-therapy', 'assessment', 'student'],
    accommodations: ['Visual schedules', 'Token reward stars', 'Solfeggio calming audio', 'Predictable spatial layout'],
    sensoryPreset: {
      dwellMs: 300,
      fontMode: 'standard',
      contrastMode: 'standard',
      ambientSoundscape: 'Lavender Snoezelen Calm'
    }
  },
  {
    id: 'speech-language',
    name: 'Speech, Language & Communication Disabilities',
    shortName: 'Speech & Language',
    icon: Volume2,
    color: 'from-blue-500 to-indigo-600',
    badgeColor: 'bg-blue-100 text-blue-900 border-blue-300',
    rpwdAlignment: 'Speech and Language Disability (RPwD 2016)',
    dsmCode: '315.39 (F80.9)',
    icfCode: 'b310 (Voice functions), b320 (Articulation functions), d330 (Speaking)',
    departmentName: 'Department of Speech Pathology & Augmentative Communication',
    targetAssessments: ['GFTA-3 Goldman-Fristoe Test of Articulation', 'CELF-5 Language Fundamentals', 'SALT Language Sample'],
    description: 'Assistive speech synthesis, motor planning core vocabulary, and articulation waveform therapy.',
    clinicalGoals: [
      'Rapid phrase formulation using high-frequency core vocabulary',
      'Phonetic articulation training with real-time waveform biofeedback',
      'Multi-lingual voice synthesis in English, Hindi, and Marathi',
      'Multimodal communication bridging gestures and vocalizations'
    ],
    primaryModuleIds: ['aac', 'core-vocab-ai', 'speech-therapy', 'parent-comm'],
    secondaryModuleIds: ['sign-language', 'music-therapy', 'therapist', 'assessment'],
    accommodations: ['Web Speech TTS audio', 'Custom pitch/rate controls', 'LAMP motor grids', 'Spectrogram overlay'],
    sensoryPreset: {
      dwellMs: 250,
      audioVoiceLang: 'en-US',
      ambientSoundscape: 'Acoustic Clarity Mode'
    }
  },
  {
    id: 'locomotor-physical',
    name: 'Locomotor, Motor & Physical Disabilities',
    shortName: 'Physical & Motor',
    icon: Activity,
    color: 'from-emerald-500 to-teal-600',
    badgeColor: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    rpwdAlignment: 'Locomotor Disability, Cerebral Palsy, Muscular Dystrophy, Dwarfism, Leprosy Cured (RPwD 2016)',
    dsmCode: '343.9 (G80.9 Cerebral Palsy)',
    icfCode: 'b760 (Control of voluntary movement), d450 (Walking), d440 (Fine hand use)',
    departmentName: 'Department of Physical Therapy & Locomotor Rehabilitation',
    targetAssessments: ['FIM & WeeFIM Functional Independence', 'GMFCS Gross Motor Function', 'MACS Manual Ability'],
    description: 'Aquatic buoyancy therapy, bionic myoelectric fitting, and physical independence measurement.',
    clinicalGoals: [
      'Spasticity reduction and joint mobility through aquatic hydrotherapy',
      'Myoelectric calibration and range-of-motion fitting for bionic limbs',
      'Functional independence scoring (FIM) for self-care and transfers',
      'Eye-gaze dwell timing for students with severe manual motor limitations'
    ],
    primaryModuleIds: ['hydrotherapy', 'prosthetics', 'fim-weefim', 'hospital'],
    secondaryModuleIds: ['aac', 'school-erp', 'inventory', 'tele-rehab'],
    accommodations: ['200ms-800ms dwell selection', 'Large tap targets (min 48px)', 'Wheelchair route tracking'],
    sensoryPreset: {
      dwellMs: 500,
      contrastMode: 'standard',
      ambientSoundscape: 'Hydro-Calm Buoyancy'
    }
  },
  {
    id: 'neurological-stroke',
    name: 'Neurological, Brain Injury & Stroke Conditions',
    shortName: 'Neuro-Rehab & TBI',
    icon: Cpu,
    color: 'from-purple-500 to-indigo-600',
    badgeColor: 'bg-purple-100 text-purple-900 border-purple-300',
    rpwdAlignment: 'Chronic Neurological Conditions, Multiple Sclerosis, Parkinson\'s Disease (RPwD 2016)',
    dsmCode: '854.00 (S06.9X0D TBI), G20 (Parkinson\'s)',
    icfCode: 'b144 (Memory functions), b770 (Gait pattern functions), d160 (Focusing attention)',
    departmentName: 'Department of Neuro-Rehabilitation & TBI Recovery',
    targetAssessments: ['FIM Cognitive & Motor Battery', 'MoCA Montreal Cognitive Assessment', 'Timed Up & Go (TUG)'],
    description: 'WebGL gamified visual-motor neuro-rehab, rhythmic auditory gait retraining, and cognitive recovery.',
    clinicalGoals: [
      'Visual-motor coordination latency training with millisecond precision',
      'Rhythmic Auditory Stimulation (RAS) metronome pacers for Parkinson\'s gait freezing',
      'Cognitive and motor functional regain tracking via FIM batteries',
      'Encrypted remote video tele-rehabilitation for home-based therapy'
    ],
    primaryModuleIds: ['gamified-neuro', 'music-therapy', 'fim-weefim', 'tele-rehab'],
    secondaryModuleIds: ['therapist', 'ai', 'hospital', 'assessment'],
    accommodations: ['Audio metronome cues (40-180 BPM)', 'Reaction time analytics', 'Bilateral integration games'],
    sensoryPreset: {
      dwellMs: 400,
      ambientSoundscape: 'Rhythmic Auditory Entrainment (80 BPM)'
    }
  },
  {
    id: 'visual-blindness',
    name: 'Visual Impairments & Total Blindness',
    shortName: 'Visual & Braille',
    icon: Compass,
    color: 'from-amber-600 to-yellow-600',
    badgeColor: 'bg-amber-100 text-amber-900 border-amber-300',
    rpwdAlignment: 'Blindness, Low Vision (RPwD 2016)',
    dsmCode: 'H54.0 (Blindness both eyes), H54.2 (Low vision)',
    icfCode: 'b210 (Seeing functions), d110 (Watching), d166 (Reading Braille)',
    departmentName: 'Department of Vision & Tactile Learning Services',
    targetAssessments: ['Nemeth Braille Proficiency Code', 'Functional Vision Assessment (FVA)', 'Orientation & Mobility (O&M)'],
    description: 'Grade 1 & 2 Nemeth Braille translation, tactile screen reader audio, and high-contrast luminance inversion.',
    clinicalGoals: [
      'Mastery of 6-dot Braille cell patterns and Nemeth math code',
      'Full auditory navigation via screen reader live regions and spoken cues',
      'High-contrast yellow-on-black viewing for low vision learners',
      'Tactile orientation and spatial mapping exercises'
    ],
    primaryModuleIds: ['braille', 'aac'],
    secondaryModuleIds: ['student', 'teacher', 'school-erp'],
    accommodations: ['High-contrast yellow/black palette', 'Screen reader live text-to-speech', 'Tactile audio markers'],
    sensoryPreset: {
      contrastMode: 'high-contrast-yellow',
      fontMode: 'standard',
      audioVoiceLang: 'en-US'
    }
  },
  {
    id: 'hearing-deaf',
    name: 'Hearing Impairments & Deafness',
    shortName: 'Hearing & Sign Language',
    icon: Eye,
    color: 'from-cyan-500 to-blue-600',
    badgeColor: 'bg-cyan-100 text-cyan-900 border-cyan-300',
    rpwdAlignment: 'Hearing Impairment (Deaf & Hard of Hearing), Multiple Disabilities including Deafblindness (RPwD 2016)',
    dsmCode: 'H90.3 (Sensorineural hearing loss, bilateral)',
    icfCode: 'b230 (Hearing functions), d115 (Listening), d315 (Nonverbal messages)',
    departmentName: 'Department of Audiology & Deaf Education',
    targetAssessments: ['Standardized Indian Sign Language (ISL) Lexicon', 'Speech Discrimination Score (SDS)', 'ASL Gesture Rubric'],
    description: 'Indian Sign Language (ISL) & ASL video recognition, fingerspelling cards, and visual spectrograms.',
    clinicalGoals: [
      'Vocabulary acquisition through standardized ISL & ASL video gestures',
      'Gesture recognition camera practice for fingerspelling clarity',
      'Visual sound spectrograms to visually monitor speech volume and pitch',
      'Tactile Braille communication bridge for deafblind individuals'
    ],
    primaryModuleIds: ['audiometry-hearing', 'sign-language', 'braille'],
    secondaryModuleIds: ['speech-therapy', 'sensory-room', 'teacher'],
    accommodations: ['Visual status indicators', 'Color-coded visual alerts', 'Multi-speed video demonstration'],
    sensoryPreset: {
      dwellMs: 250,
      contrastMode: 'standard'
    }
  },
  {
    id: 'learning-cognitive',
    name: 'Specific Learning & Cognitive Disabilities',
    shortName: 'Dyslexia & Learning',
    icon: GraduationCap,
    color: 'from-emerald-600 to-green-700',
    badgeColor: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    rpwdAlignment: 'Specific Learning Disabilities (Dyslexia, Dyscalculia, Dysgraphia), Intellectual Disability (RPwD 2016)',
    dsmCode: '315.00 (F81.0 Specific reading impairment / Dyslexia)',
    icfCode: 'b140 (Attention functions), b167 (Mental functions of language), d140 (Learning to read)',
    departmentName: 'Department of Learning Support & Remedial Education',
    targetAssessments: ['GORT-5 Gray Oral Reading Tests', 'CTOPP-2 Comprehensive Test of Phonological Processing', 'WISC-V Cognitive Battery'],
    description: 'OpenDyslexic typography, Universal Design for Learning (UDL) planners, and micro-goal breakdown.',
    clinicalGoals: [
      'Reading comprehension enhancement using weighted OpenDyslexic letterforms',
      'Differentiated classroom instruction tailored to individual cognitive paces',
      'IEP SMART milestone tracking with actionable bite-sized sub-tasks',
      'AI-powered simplification of complex educational narratives'
    ],
    primaryModuleIds: ['teacher', 'student', 'ai'],
    secondaryModuleIds: ['autism', 'assessment', 'parent-comm'],
    accommodations: ['OpenDyslexic font mode', 'Text-to-Speech audio reader', 'Chunked visual instructions'],
    sensoryPreset: {
      fontMode: 'opendyslexic',
      dwellMs: 200,
      audioVoiceLang: 'en-US'
    }
  },
  {
    id: 'transition-vocational',
    name: 'Transition, Vocational & Independent Living',
    shortName: 'Vocational & Transition',
    icon: Briefcase,
    color: 'from-slate-700 to-slate-900',
    badgeColor: 'bg-slate-100 text-slate-900 border-slate-300',
    rpwdAlignment: 'All 21 RPwD Categories Transitioning to Adulthood & Competitive Employment',
    dsmCode: 'Z73.0 (Problems related to life-management difficulty)',
    icfCode: 'd845 (Acquiring, keeping and terminating a job), d850 (Remunerative employment), d640 (Doing housework)',
    departmentName: 'Department of Secondary Transition & Vocational Living',
    targetAssessments: ['Indicator 13 Transition Checklist', 'Casey Life Skills Assessment', 'TPI-2 Transition Planning Inventory'],
    description: 'Secondary transition readiness, Activities of Daily Living (ADL), and community visit verification.',
    clinicalGoals: [
      'Indicator 13 special education secondary transition compliance audit',
      'Activities of Daily Living (cooking, money management, personal hygiene)',
      'Electronic Visit Verification (EVV) with real-time GPS for caregiver check-in',
      'NDIS & disability scheme self-managed funding allocation'
    ],
    primaryModuleIds: ['transition-matrix', 'vocational-living', 'evv-gps-tracker', 'billing'],
    secondaryModuleIds: ['analytics', 'hospital', 'inventory', 'student'],
    accommodations: ['Mobile smartphone responsive views', 'Step-by-step ADL photo prompts', 'GPS clock-in'],
    sensoryPreset: {
      dwellMs: 250,
      ambientSoundscape: 'Workplace Focus Binaural'
    }
  }
];

export function getDisabilityCategoryById(id: string): DisabilityCategory | undefined {
  return DISABILITY_CLUSTERS.find(c => c.id === id);
}

export function getDisabilityForStudent(diagnosis: string): DisabilityCategory {
  const diagLower = diagnosis.toLowerCase();
  if (diagLower.includes('autism') || diagLower.includes('asd') || diagLower.includes('aba')) {
    return DISABILITY_CLUSTERS[0];
  }
  if (diagLower.includes('speech') || diagLower.includes('articulation') || diagLower.includes('apraxia')) {
    return DISABILITY_CLUSTERS[1];
  }
  if (diagLower.includes('cerebral palsy') || diagLower.includes('motor') || diagLower.includes('muscular')) {
    return DISABILITY_CLUSTERS[2];
  }
  if (diagLower.includes('brain injury') || diagLower.includes('tbi') || diagLower.includes('stroke') || diagLower.includes('parkinson')) {
    return DISABILITY_CLUSTERS[3];
  }
  if (diagLower.includes('visual') || diagLower.includes('blind') || diagLower.includes('braille')) {
    return DISABILITY_CLUSTERS[4];
  }
  if (diagLower.includes('hearing') || diagLower.includes('deaf') || diagLower.includes('sign')) {
    return DISABILITY_CLUSTERS[5];
  }
  if (diagLower.includes('dyslexia') || diagLower.includes('learning') || diagLower.includes('down syndrome')) {
    return DISABILITY_CLUSTERS[6];
  }
  return DISABILITY_CLUSTERS[7];
}
